// ─── Currency ────────────────────────────────────────────────────────────────

export function formatDollars(cents: number): string {
  const safe = Number.isFinite(cents) ? cents : 0;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(safe / 100);
}

// ─── Rarity ──────────────────────────────────────────────────────────────────

const RARITY_COLOR_MAP: Record<string, string> = {
  'Consumer Grade':   'text-slate-400',
  'Industrial Grade': 'text-sky-400',
  'Mil-Spec Grade':   'text-blue-500',
  'Restricted':       'text-purple-500',
  'Classified':       'text-pink-500',
  'Covert':           'text-red-500',
  'Contraband':       'text-amber-500',
  'Base Grade':       'text-slate-400',
  'High Grade':       'text-sky-400',
  'Remarkable':       'text-purple-500',
  'Extraordinary':    'text-red-500',
};

export function rarityColor(rarity: string): string {
  return RARITY_COLOR_MAP[rarity] ?? 'text-slate-400';
}

const RARITY_DOT_MAP: Record<string, string> = {
  'Consumer Grade':   'bg-slate-400',
  'Industrial Grade': 'bg-sky-400',
  'Mil-Spec Grade':   'bg-blue-500',
  'Restricted':       'bg-purple-500',
  'Classified':       'bg-pink-500',
  'Covert':           'bg-red-500',
  'Contraband':       'bg-amber-500',
  'Base Grade':       'bg-slate-400',
  'High Grade':       'bg-sky-400',
  'Remarkable':       'bg-purple-500',
  'Extraordinary':    'bg-red-500',
};

export function rarityDotColor(rarity: string): string {
  return RARITY_DOT_MAP[rarity] ?? 'bg-slate-400';
}

// ─── Wear ─────────────────────────────────────────────────────────────────────

const WEAR_MAP: Record<string, string> = {
  'Factory New':        'FN',
  'Minimal Wear':       'MW',
  'Field-Tested':       'FT',
  'Well-Worn':          'WW',
  'Battle-Scarred':     'BS',
};

export function wearAbbrev(wear: string): string {
  return WEAR_MAP[wear] ?? wear;
}

// ─── Order status ─────────────────────────────────────────────────────────────

export interface StatusBadgeInfo {
  label: string;
  class: string;
}

const STATUS_MAP: Record<string, StatusBadgeInfo> = {
  pending:     { label: 'Pending',     class: 'bg-amber-50 text-amber-700 border border-amber-200' },
  processing:  { label: 'Processing',  class: 'bg-blue-50 text-blue-700 border border-blue-200' },
  purchasing:  { label: 'Purchasing',  class: 'bg-blue-50 text-blue-700 border border-blue-200' },
  purchased:   { label: 'Purchased',   class: 'bg-blue-50 text-blue-700 border border-blue-200' },
  withdrawing: { label: 'Withdrawing', class: 'bg-blue-50 text-blue-700 border border-blue-200' },
  trade_sent:  { label: 'Trade Sent',  class: 'bg-green-50 text-green-700 border border-green-200' },
  delivered:   { label: 'Delivered',   class: 'bg-green-50 text-green-700 border border-green-200' },
  completed:   { label: 'Completed',   class: 'bg-green-50 text-green-700 border border-green-200' },
  failed:      { label: 'Failed',      class: 'bg-red-50 text-red-700 border border-red-200' },
  refunded:    { label: 'Refunded',    class: 'bg-red-50 text-red-700 border border-red-200' },
  cancelled:   { label: 'Cancelled',   class: 'bg-slate-100 text-slate-600 border border-slate-200' },
};

export function statusBadge(status: string): StatusBadgeInfo {
  return STATUS_MAP[status.toLowerCase()] ?? {
    label: status,
    class: 'bg-slate-100 text-slate-600 border border-slate-200',
  };
}

export const TERMINAL_STATUSES = new Set([
  'completed',
  'delivered',
  'failed',
  'refunded',
  'cancelled',
]);

// ─── Date / time ──────────────────────────────────────────────────────────────

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}
